import React, { useState } from 'react';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DesktopTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useFormContext, Controller } from 'react-hook-form';
import { get } from 'lodash';
import { cn } from '@src/utils/ui';
import { KeyboardArrowDown } from '@mui/icons-material';
import PropTypes from 'prop-types';
import moment from 'moment';

const theme = createTheme({
    typography: { fontSize: 14, fontFamily: 'BCAASans, sans-serif' },
    palette: {
        primary: { main: '#0050c4' }
    },
    components: {
        MuiInputBase: {
            styleOverrides: {
                root: { fontSize: '14px' }
            }
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontSize: '14px',
                    '&.Mui-focused': {
                        color: '#0050c4'
                    }
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0050c4'
                    }
                }
            }
        },
        MuiInput: {
            styleOverrides: {
                root: {
                    '&.Mui-focused': {
                        borderColor: '#0050c4'
                    }
                }
            }
        },
        MuiPickersLayout: {
            styleOverrides: {
                root: { fontSize: '14px' }
            }
        },
        MuiMenuItem: {
            styleOverrides: {
                root: { fontSize: '14px' }
            }
        },
        MuiButtonBase: {
            styleOverrides: {
                root: { fontSize: '14px' }
            }
        },
        MuiTypography: {
            styleOverrides: {
                root: { fontSize: '14px' }
            }
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    fontSize: '12px',
                    fontWeight: 400
                }
            }
        }
    }
});

const PickerIcon = () => <KeyboardArrowDown size={20} />;

export const TimeInput = ({
    name,
    InputProps = {},
    FormHelperTextProps = {},
    disabledTime = [],
    selectedDate = moment(),
    onChange,
    ...rest
}) => {
    const { control, formState, setError } = useFormContext();
    const error = !!get(formState.errors, name);
    const helperText = get(formState.errors, `${name}.message`);
    const [open, setOpen] = useState(false);

    const handleClick = e => {
        e.preventDefault();
        e.stopPropagation();
        setOpen(true);
    };

    const shouldDisableTime = (value, view) => {
        if (!selectedDate || !disabledTime) return false;

        const day = selectedDate.day();
        const dateStr = selectedDate.format('YYYY-MM-DD');

        const recurringBlocks = disabledTime.recurring?.[day] || [];
        const absoluteBlocks = disabledTime.absolute?.[dateStr] || [];
        const allBlocks = [...recurringBlocks, ...absoluteBlocks];

        // Normalize blocks into full datetime ranges
        const blockedRanges = allBlocks.map(({ startTime, endTime }) => {
            const [sh, sm, ss = 0] = startTime.split(':').map(Number);
            const [eh, em, es = 0] = endTime.split(':').map(Number);

            const start = selectedDate.clone().set({
                hour: sh,
                minute: sm,
                second: ss,
                millisecond: 0
            });

            let end = selectedDate.clone().set({
                hour: eh,
                minute: em,
                second: es,
                millisecond: 0
            });

            // Extend end by 1 minute if block ends at exactly 23:59
            if (eh === 23 && em === 59) {
                end.add(1, 'minute');
            }

            return { start, end };
        });

        if (view === 'minutes') {
            const time = selectedDate.clone().set({
                hour: value.hour(),
                minute: value.minute(),
                second: 0,
                millisecond: 0
            });

            return blockedRanges.some(({ start, end }) => time.isBetween(start, end, undefined, '[)'));
        }

        if (view === 'hours') {
            const hour = value.hour();
            let allMinutesBlocked = true;

            for (let minute = 0; minute < 60; minute++) {
                const time = selectedDate.clone().set({
                    hour,
                    minute,
                    second: 0,
                    millisecond: 0
                });

                const isBlocked = blockedRanges.some(({ start, end }) => time.isBetween(start, end, undefined, '[)'));

                if (!isBlocked) {
                    allMinutesBlocked = false;
                    break;
                }
            }

            return allMinutesBlocked;
        }

        return false;
    };

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <LocalizationProvider dateAdapter={AdapterMoment}>
                <Controller
                    name={name}
                    control={control}
                    render={({ field }) => {
                        const handleChange = date => {
                            if (!date || !selectedDate) return;

                            const testTime = selectedDate.clone().set({
                                hour: date.hour(),
                                minute: date.minute(),
                                second: 0
                            });

                            let isBlocked = shouldDisableTime(testTime, 'minutes');

                            if (!isBlocked && rest.minTime) {
                                const limitTime = selectedDate.clone().set({
                                    hour: rest.minTime.hour(),
                                    minute: rest.minTime.minute(),
                                    second: 0
                                });

                                if (testTime.isBefore(limitTime)) isBlocked = true;
                            }

                            if (isBlocked) {
                                console.warn('Blocked time selected.');
                                const timer = setTimeout(() => {
                                    setError(name, {
                                        type: 'manual',
                                        message: 'Selected time is not available.'
                                    });
                                    clearTimeout(timer);
                                }, 0);
                            }
                            if (onChange) onChange(date);
                            field.onChange(date);
                        };

                        return (
                            <DesktopTimePicker
                                open={open}
                                onOpen={() => setOpen(true)}
                                onClose={() => setOpen(false)}
                                format='h:mm A'
                                {...rest}
                                value={field.value}
                                onChange={handleChange}
                                shouldDisableTime={shouldDisableTime}
                                slotProps={{
                                    field: { shouldRespectLeadingZeros: true },
                                    textField: {
                                        onClick: handleClick,
                                        error,
                                        helperText,
                                        InputProps: {
                                            placeholder: 'h:mm A',
                                            ...InputProps,
                                            className: cn('tw-text-[14px]', InputProps.className)
                                        },
                                        FormHelperTextProps: {
                                            ...FormHelperTextProps,
                                            className: cn('tw-text-[12px]', FormHelperTextProps.className)
                                        }
                                    },
                                    actionBar: {
                                        actions: []
                                    }
                                }}
                                slots={{
                                    openPickerIcon: PickerIcon
                                }}
                            />
                        );
                    }}
                />
            </LocalizationProvider>
        </ThemeProvider>
    );
};

TimeInput.propTypes = {
    name: PropTypes.string.isRequired,
    InputProps: PropTypes.object,
    FormHelperTextProps: PropTypes.object,
    disabledTime: PropTypes.object,
    selectedDate: PropTypes.object,
    onChange: PropTypes.func
};
